import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import classNames from 'classnames';

import { IAsyncOption } from './constants';
import FormFieldError from './FormFieldError';
import FormFieldLabel from './FormFieldLabel';
import FormFieldTooltip, { TooltipSource } from './FormFieldTooltip';
import { FormProvider } from './FormProvider';
import { ReactComponent as CaretIcon } from './icons/caret.svg';
import useFormField, { FieldEvent, FieldWidth } from './useFormField';

interface IProps {
  name: string;
  label?: string;
  tooltip?: TooltipSource;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  width?: FieldWidth;
  loadOptions: (filter: string) => Promise<IAsyncOption[]>;
  entityToOption: (entity: any) => IAsyncOption;
  autoFocus?: boolean;
  minCharacters?: number;
  onFocus?: FieldEvent<string[]>;
  onChange?: FieldEvent<string[]>;
  onBlur?: FieldEvent<string[]>;
}

enum FeedbackMessage {
  NoOption = 'noOptions',
  MinCharacters = 'minCharacters',
}

function FormMultiAsyncSelectField({
  name,
  label,
  tooltip,
  description,
  disabled,
  width,
  loadOptions,
  entityToOption,
  onFocus,
  onChange,
  onBlur,
  autoFocus = false,
  placeholder = '',
  minCharacters = 2,
}: IProps) {
  const {
    id,
    fieldProps,
    inputProps,
    setFieldValue,
    setFieldTouched,
    required,
    disabled: formDisabled,
  } = useFormField<string[]>(name, width, { onFocus, onChange, onBlur }, autoFocus);
  const { t } = useTranslation();
  const [noOptionMessage, setNoOptionMessage] = useState(FeedbackMessage.MinCharacters);
  const { labelsNamespace } = useContext(FormProvider);

  const onChangeHandler = (values: any[]) => {
    if (disabled) {
      return;
    }
    setNoOptionMessage(FeedbackMessage.MinCharacters);
    setFieldValue(name, values, true);
  };

  const onInput = (input) => {
    if (input && input.length >= minCharacters) {
      setNoOptionMessage(FeedbackMessage.NoOption);
      return loadOptions(input);
    } else {
      setNoOptionMessage(FeedbackMessage.MinCharacters);
      return Promise.resolve([]);
    }
  };

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <CaretIcon />
      </components.DropdownIndicator>
    );
  };

  const selectedValues = inputProps.value || [];
  const values = selectedValues.map((value: any) => {
    if (value.label && value.id) return value;
    else return entityToOption(value);
  });

  const fieldClassName = classNames(fieldProps.className, 'novoFormField--select');
  return (
    <div {...fieldProps} className={fieldClassName}>
      {label && (
        <div className="novoFormField__name">
          <FormFieldLabel for={id} required={required}>
            {label}
          </FormFieldLabel>
          <FormFieldTooltip source={tooltip} />
        </div>
      )}
      {description && <p className="novoFormField__description">{description}</p>}
      <AsyncSelect
        id={id}
        className={'novoInputMultiSelect'}
        classNamePrefix={'novoInputMultiSelect'}
        closeMenuOnSelect={true}
        components={{ DropdownIndicator }}
        onFocus={inputProps.onFocus}
        onChange={onChangeHandler}
        onBlur={() => setFieldTouched(name, true)}
        isDisabled={formDisabled || disabled}
        loadOptions={onInput}
        value={values}
        placeholder={placeholder}
        isMulti
        noOptionsMessage={() => t(`${labelsNamespace}.${noOptionMessage}`, { count: minCharacters })}
        loadingMessage={() => t(`${labelsNamespace}.loadingOptions`)}
      />
      <FormFieldError name={name} />
    </div>
  );
}

export default FormMultiAsyncSelectField;
